import { Search } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox } from '@/components/ui/checkbox';

import { checkboxClass, toggledSet } from './lib';

type Props<T extends string> = {
  label: string;
  /** Copy for the select-all checkbox, e.g. "All services". */
  allLabel: string;
  items: { id: T; label: string }[];
  selected: ReadonlySet<T>;
  onSelectionChange: (next: Set<T>) => void;
};

/**
 * Checklist for one exclusion scope (methods or countries), with a select-all
 * checkbox that ticks every option rather than standing in for them.
 *
 * Ticking everything is submitted as a full enumeration and the backend collapses
 * it to the stored empty "all of them" sentinel, which is what keeps a method or
 * country added to the catalog later covered by an exclusion saved today. Because
 * of that, the reverse also holds: a stored blanket exclusion is seeded into this
 * picker with every box ticked (see the dialogs), so what the merchant sees
 * matches what is enforced.
 *
 * Select-all renders as the list's header row, pinned above the scroll area, and
 * deliberately ignores the search box: it applies to every option, not just the
 * ones currently matching the filter. That matters most when a filter is active,
 * since the header then covers more than the rows visible under it.
 */
export function ExclusionScopePicker<T extends string>({
  label,
  allLabel,
  items,
  selected,
  onSelectionChange,
}: Props<T>) {
  const { t } = useTranslation('excluded-products');
  const [search, setSearch] = useState('');

  const allChecked =
    items.length > 0 && items.every((item) => selected.has(item.id))
      ? true
      : selected.size > 0
        ? 'indeterminate'
        : false;

  const filtered = items.filter((item) =>
    item.label.toLowerCase().includes(search.trim().toLowerCase()),
  );

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm font-medium leading-none text-black">{label}</p>

      <div className="flex h-9 items-stretch">
        <input
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          aria-label={label}
          className="min-w-0 flex-1 rounded-l-md border-y border-l border-input bg-white px-3 text-sm leading-5 placeholder:text-muted-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
        <span
          className="inline-flex w-9 shrink-0 items-center justify-center rounded-r-md border-y border-r border-input bg-white"
          aria-hidden="true"
        >
          <Search className="size-4" />
        </span>
      </div>

      <div>
        {/*
          Select-all sits above the scroll area, so it stays put while the list
          scrolls and reads as the list's header row rather than another option.
          Its column widths and type match the header of the main products table.
        */}
        <label className="flex h-10 cursor-pointer items-center border-b">
          <span className="flex w-9 shrink-0 items-center justify-center p-2">
            <Checkbox
              className={checkboxClass}
              checked={allChecked}
              onCheckedChange={(checked) =>
                onSelectionChange(
                  checked === true ? new Set(items.map((item) => item.id)) : new Set(),
                )
              }
            />
          </span>
          <span className="min-w-0 flex-1 truncate p-2 text-xs font-medium leading-none text-foreground">
            {allLabel}
          </span>
        </label>

        <div className="max-h-[156px] overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="py-4 text-sm text-muted-foreground">{t('dialog.noResults')}</p>
          ) : (
            filtered.map((item) => (
              <label key={item.id} className="flex h-[52px] cursor-pointer items-center">
                <span className="flex w-9 shrink-0 items-center justify-center p-2">
                  <Checkbox
                    className={checkboxClass}
                    checked={selected.has(item.id)}
                    onCheckedChange={(checked) =>
                      onSelectionChange(toggledSet(selected, item.id, checked === true))
                    }
                  />
                </span>
                <span className="flex h-full min-w-0 flex-1 items-center border-b p-2 text-base leading-6 text-foreground">
                  <span className="truncate">{item.label}</span>
                </span>
              </label>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
